/**
 * Client-side CSRF helper.
 *
 * Installs a global `fetch` wrapper that mirrors the `XSRF-TOKEN` cookie
 * (set by the server's CSRF middleware) into the `X-CSRF-Token` header on
 * same-origin unsafe API requests. The double-submit pattern means a
 * cross-origin attacker cannot forge the header even if they trick the
 * browser into making a request, because they cannot read the cookie.
 *
 * Idempotent: re-importing this module is a no-op.
 */
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const CSRF_COOKIE = "XSRF-TOKEN";
const CSRF_HEADER = "X-CSRF-Token";

export function getCsrfToken(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  for (const c of cookies) {
    const idx = c.indexOf("=");
    if (idx < 0) continue;
    const key = c.slice(0, idx).trim();
    if (key === CSRF_COOKIE) {
      return decodeURIComponent(c.slice(idx + 1).trim());
    }
  }
  return undefined;
}

function isSameOriginApi(url: string): boolean {
  if (url.startsWith("/api/")) return true;
  if (typeof window === "undefined") return false;
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

let installed = false;

export function installCsrfFetchInterceptor(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = (init?.method || (typeof input !== "string" && !(input instanceof URL) ? input.method : "GET") || "GET").toUpperCase();
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

    if (SAFE_METHODS.has(method) || !isSameOriginApi(url)) {
      return orig(input as RequestInfo, init);
    }

    const token = getCsrfToken();
    const headers = new Headers(init?.headers || (typeof input !== "string" && !(input instanceof URL) ? input.headers : undefined));
    if (token && !headers.has(CSRF_HEADER)) {
      headers.set(CSRF_HEADER, token);
    }
    const merged: RequestInit = { ...init, headers, credentials: init?.credentials ?? "include" };
    return orig(input as RequestInfo, merged);
  };
}

/**
 * Best-effort: hit a safe endpoint so the server seeds the XSRF-TOKEN
 * cookie before the user attempts the first unsafe request after a fresh
 * page load with no cookie.
 */
export async function ensureCsrfToken(): Promise<void> {
  if (getCsrfToken()) return;
  try {
    await fetch("/api/csrf-token", { credentials: "include" });
  } catch {
    // Server may be cold-starting; the next safe GET will set the cookie.
  }
}
